import { useCallback, useEffect, useState } from "react";
import { pushSupported, getPushConfig, getCurrentPushStatus } from "@/lib/push";

/**
 * usePushNotifications — state-only hook. Owns { state, busy, refresh,
 * setBusy } so callers can wrap the raw mutations from `@/lib/push` with
 * their own copy / analytics / redirects. Kept intentionally thin: the
 * various pages that surface push toggles (Profile, potentially Settings
 * or Household admin) have slightly different UX voices and it's not
 * worth pushing that into the hook.
 *
 *   const { state, busy, setBusy, refresh } = usePushNotifications();
 *   const onToggle = async () => {
 *     setBusy(true);
 *     try {
 *       state.subscribed ? await disablePush() : await enablePush();
 *       // custom toast here
 *       await refresh();
 *     } finally { setBusy(false); }
 *   };
 */
export default function usePushNotifications() {
  const [state, setState] = useState({
    supported: false,
    permission: "default",
    subscribed: false,
    mocked: true,
    hasKeys: false,
  });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!pushSupported()) {
      setState({
        supported: false,
        permission: "unsupported",
        subscribed: false,
        mocked: true,
        hasKeys: false,
      });
      return;
    }
    try {
      const [status, cfg] = await Promise.all([getCurrentPushStatus(), getPushConfig()]);
      setState({ ...status, mocked: !!cfg.mocked, hasKeys: !!cfg.public_key });
    } catch (e) {
      console.error("push refresh failed:", e);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { state, busy, setBusy, refresh };
}
